import { getAsset } from '@/lib/assets';
import type { Manifest } from '@/lib/manifest-fetcher';

import type {
  ButtonBlock,
  GradientHeroButton,
  GradientHeroSearchBar,
  GradientHeroSectionData,
  HeroButtonAction,
  TextBlock,
} from './GradientHeroSection.types';

// ── Raw JSON shape (mirrors gradientHeroSection.json) ────────────────────────

interface RawButton {
  title?: unknown;
  slug?: unknown;
  buttonType?: unknown;
  action?: HeroButtonAction;
}

interface RawLink {
  title?: unknown;
  slug?: unknown;
}

interface RawSearchBar {
  inputPlaceholder?: unknown;
  dropdownLabel?: unknown;
  dropdownOptions?: unknown;
  searchButtonLabel?: unknown;
}

interface RawTrustLogo {
  imageUrl?: unknown;
  title?: unknown;
  href?: unknown;
}

interface RawGradientHeroSection {
  heading?: unknown;
  description?: unknown;
  primaryButton?: RawButton;
  secondaryButton?: RawButton;
  link?: RawLink;
  searchBar?: RawSearchBar;
  trustLogos?: RawTrustLogo[];
}

// ── Validation helpers ────────────────────────────────────────────────────────

const isString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === 'string');

// ── Transformers (raw JSON -> typed data) ────────────────────────────────────

const parseButton = (
  raw: RawButton | undefined,
): GradientHeroButton | undefined => {
  if (!raw) return undefined;

  const content = isString(raw.title) ? raw.title : undefined;
  const link = isString(raw.slug) ? raw.slug : undefined;

  if (!content || !link) return undefined;

  const buttonType = isString(raw.buttonType) ? raw.buttonType : 'primary';

  return {
    content,
    link,
    buttonType,
    styles: {},
    action: raw.action,
  };
};

const parseLink = (raw: RawLink | undefined): TextBlock | undefined => {
  if (!raw) return undefined;
  const slug = isString(raw.slug) ? raw.slug : undefined;
  if (!slug) return undefined;
  const title = isString(raw.title) ? raw.title : slug;
  return { content: `[${title}](${slug})` };
};

const parseSearchBar = (
  raw: RawSearchBar | undefined,
): GradientHeroSearchBar | undefined => {
  if (!raw) return undefined;
  return {
    inputPlaceholder: isString(raw.inputPlaceholder)
      ? raw.inputPlaceholder
      : 'Search Consultants by Name, Speciality or Condition....',
    dropdownLabel: isString(raw.dropdownLabel)
      ? raw.dropdownLabel
      : 'Specialities',
    dropdownOptions: isStringArray(raw.dropdownOptions)
      ? raw.dropdownOptions
      : ['Specialities'],
    searchButtonLabel: isString(raw.searchButtonLabel)
      ? raw.searchButtonLabel
      : 'Search',
  };
};

const parseTrustLogos = (
  manifest: Manifest,
  raw: RawTrustLogo[] | undefined,
): TextBlock[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (logo): logo is RawTrustLogo => logo !== null && typeof logo === 'object',
    )
    .map((logo) => {
      const rawUrl = isString(logo.imageUrl) ? logo.imageUrl : '';
      const resolvedUrl =
        rawUrl && !rawUrl.startsWith('http')
          ? getAsset(manifest, rawUrl)
          : rawUrl;
      const imageUrl = resolvedUrl ?? rawUrl;
      if (!imageUrl) return null;
      const title = isString(logo.title) ? logo.title : '';
      const href = isString(logo.href) ? logo.href : '#';
      return {
        content: `[![${title}](${imageUrl} "91x49")](${href})`,
      } as TextBlock;
    })
    .filter((logo): logo is TextBlock => logo !== null);
};

// ── Public API ────────────────────────────────────────────────────────────────

const rawGradientHeroData = {
  heading: 'Your Health Matters.',
  description:
    'Doctor-led care from over 100 world-class consultants at Battersea Power Station. Whether you need expert answers today or want to stay ahead of illness, LIPS offers expert care on your terms.\n\nSeven days a week.',
  primaryButton: {
    title: 'Urgent Care',
    slug: '/specialities/urgent-care-centre',
    functionality: 'internal_link',
  },
  secondaryButton: {
    title: 'Book a GP',
    slug: '/specialities/private-gp',
    buttonType: 'secondary',
  },
  action: {
    type: 'scrollOnNextPage',
    targetId: 'booking-calendar-widget',
    offset: 112,
    focus: true,
  },
  link: {
    title: 'See Someone Today? Find Your Specialist',
    slug: '/our-specialists',
  },
  searchBar: {
    inputPlaceholder: 'Search Consultants by Name, Speciality or Condition....',
    dropdownLabel: 'General',
    dropdownOptions: [
      'General',
      'Tests & Scans',
      'Primary Care',
      'Surgical Speciality',
    ],
    searchButtonLabel: 'Search',
  },
  trustLogos: [
    {
      imageUrl: 'top-doctors-logo.webp',
      title: 'Top Doctors',
      href: 'https://www.topdoctors.co.uk/gh/lips-healthcare/',
    },
    {
      imageUrl: 'trustpilot-logo-reviews.webp',
      title: 'Trustpilot',
      href: 'https://uk.trustpilot.com/review/lips.org.uk?utm_medium=trustbox&utm_source=Mini',
    },
    {
      imageUrl: 'doctify-laurel-badge.webp',
      title: 'Doctify',
      href: 'https://www.doctify.com/uk/practice/lips-battersea-power-station',
    },
  ],
};

export const getGradientHeroSectionData = (
  manifest: Manifest,
): GradientHeroSectionData => {
  const raw = rawGradientHeroData as unknown as RawGradientHeroSection;

  const buttons = [
    parseButton(raw.primaryButton),
    parseButton(raw.secondaryButton),
  ].filter((btn): btn is ButtonBlock => btn !== undefined);

  return {
    heading: isString(raw.heading)
      ? { content: `# ${raw.heading}` }
      : undefined,
    description: isString(raw.description)
      ? { content: raw.description }
      : undefined,
    buttons: buttons.length > 0 ? buttons : undefined,
    link: parseLink(raw.link),
    searchBar: parseSearchBar(raw.searchBar),
    trustLogos: parseTrustLogos(manifest, raw.trustLogos),
  };
};

export const normalizeGradientHeroSectionData = (
  data: GradientHeroSectionData,
): GradientHeroSectionData => {
  const warn = (field: string, reason: string) => {
    if (process.env.NODE_ENV === 'development') {
      console.warn(
        `[GradientHeroSection] "${field}" will not render — ${reason}.`,
      );
    }
  };

  const normalizeTextBlock = (
    block: TextBlock | undefined,
    fieldName: string,
  ): TextBlock | undefined => {
    if (!block) return undefined;
    if (!isString(block.content)) {
      warn(fieldName, '"content" is missing or empty');
      return undefined;
    }
    return block;
  };

  const normalizeButtons = (
    btns: ButtonBlock[] | undefined,
  ): ButtonBlock[] | undefined => {
    if (!Array.isArray(btns)) return undefined;
    const valid = btns.filter((btn) => {
      if (!isString(btn.content)) {
        warn('buttons entry', '"content" is missing or empty');
        return false;
      }
      return true;
    });
    return valid.length > 0 ? valid : undefined;
  };

  const normalizeSearchBar = (
    bar: GradientHeroSearchBar | undefined,
  ): GradientHeroSearchBar | undefined => {
    if (!bar || typeof bar !== 'object') {
      if (bar !== undefined) warn('searchBar', 'value is not an object');
      return undefined;
    }
    return {
      inputPlaceholder: isString(bar.inputPlaceholder)
        ? bar.inputPlaceholder
        : 'Search Consultants by Name, Speciality or Condition....',
      dropdownLabel: isString(bar.dropdownLabel)
        ? bar.dropdownLabel
        : 'Specialities',
      dropdownOptions: isStringArray(bar.dropdownOptions)
        ? bar.dropdownOptions
        : ['Specialities'],
      searchButtonLabel: isString(bar.searchButtonLabel)
        ? bar.searchButtonLabel
        : 'Search',
    };
  };

  const normalizeTrustLogos = (
    logos: TextBlock[] | undefined,
  ): TextBlock[] | undefined => {
    if (!Array.isArray(logos)) return undefined;
    const valid = logos.filter((logo) => {
      if (!logo || typeof logo !== 'object') return false;
      if (!isString(logo.content)) {
        warn('trustLogos entry', '"content" is missing or empty');
        return false;
      }
      return true;
    });
    if (valid.length === 0) {
      if (logos.length > 0) warn('trustLogos', 'all entries failed validation');
      return undefined;
    }
    return valid;
  };

  return {
    heading: normalizeTextBlock(data.heading, 'heading'),
    description: normalizeTextBlock(data.description, 'description'),
    badge: normalizeTextBlock(data.badge, 'badge'),
    buttons: normalizeButtons(data.buttons),
    link: normalizeTextBlock(data.link, 'link'),
    searchBar: normalizeSearchBar(data.searchBar),
    trustLogos: normalizeTrustLogos(data.trustLogos),
    images: data.images,
    textsContentData: data.textsContentData,
  };
};
